import * as React from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ChevronsUpDown } from "lucide-react";

type Option = {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
};

type MultiCheckboxDropdownProps = {
  field?: React.HTMLAttributes<HTMLDivElement>;
  options: Option[];
  onChange: (selected: Record<string, boolean>) => void;
  value: string[];
};

export const MultiCheckboxDropdown = ({
  field,
  options,
  onChange,
  value,
}: MultiCheckboxDropdownProps) => {
  const [open, setOpen] = React.useState(false);
  const [selectedProvider, setSelectedProvider] = React.useState<
    Record<string, boolean>
  >({});

  React.useEffect(() => {
    if (value) {
      const initialProviders = value.reduce(
        (acc: Record<string, boolean>, provider: string) => {
          acc[provider] = true;
          return acc;
        },
        {},
      );
      setSelectedProvider(initialProviders);
    }
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen} {...field}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          className="flex w-full min-w-[200px] cursor-pointer items-center justify-between rounded-md border-2 border-primary bg-background px-3 py-2 text-sm shadow-xs hover:bg-secondary"
        >
          <div className="flex flex-1 items-center gap-1 overflow-hidden">
            {Object.values(selectedProvider).some((val) => val) ? (
              (() => {
                const selected = Object.entries(selectedProvider).filter(
                  ([_, val]) => val,
                );
                if (selected.length > 2) {
                  return (
                    <>
                      {selected.slice(0, 2).map(([key]) => {
                        const opt = options.find((o) => o.value === key);
                        return (
                          <Badge
                            key={key}
                            variant="secondary"
                            className="shrink-0"
                          >
                            {opt?.label || key}
                          </Badge>
                        );
                      })}
                      <span className="shrink-0 text-xs text-muted-foreground">
                         +{selected.length - 2} more
                      </span>
                    </>
                  );
                }
                return selected.map(([key]) => {
                  const opt = options.find((o) => o.value === key);
                  return (
                    <Badge key={key} variant="secondary" className="shrink-0">
                      {opt?.label || key}
                    </Badge>
                  );
                });
              })()
            ) : (
              <span className="text-muted-foreground">Select</span>
            )}
          </div>

          <ChevronsUpDown className="opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        avoidCollisions={true}
        className="min-w-[--radix-popover-trigger-width] p-0"
        sideOffset={5}
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search Providers" className="h-9" />
          <CommandList>
            <CommandEmpty>No template found.</CommandEmpty>
            <CommandGroup>
              {options?.map((optionItem: Option) => (
                <CommandItem
                  key={optionItem.value}
                  value={optionItem.value}
                  disabled={optionItem.disabled}
                  onSelect={() => {
                    if (optionItem.disabled) return;
                    const isSelected = !!selectedProvider[optionItem.value];
                    const updatedProviders = {
                      ...selectedProvider,
                      [optionItem.value]: !isSelected,
                    };
                    setSelectedProvider(updatedProviders);
                    onChange(updatedProviders);
                  }}
                  className={`flex items-center gap-2 ${optionItem.disabled ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  <Checkbox
                    id={optionItem.value}
                    disabled={optionItem.disabled}
                    checked={!!selectedProvider[optionItem.value]}
                    className="border-slate-300 data-[state=checked]:border-primary dark:border-slate-600"
                    onCheckedChange={() => {
                      // Already handled by onSelect
                    }}
                  />
                  <div className="flex flex-1 items-center gap-2 overflow-hidden">
                    {optionItem.icon}
                    <label
                      htmlFor={optionItem.value}
                      className="flex-1 cursor-pointer truncate text-sm leading-none font-medium"
                      onClick={(e) => e.preventDefault()}
                    >
                      {optionItem.label}
                    </label>
                    {optionItem.disabled && (
                      <span className="rounded-sm bg-secondary px-1 text-[10px] font-bold text-muted-foreground uppercase">
                        Coming Soon
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
